// Homepage Slider admin on the carousel model (PE-1762). osprey-api reverse-proxies
// /carousel-slides (+ sub-paths, incl. GET /carousel-slides/admin and PATCH
// /carousel-slides/reorder) straight to carousel-service and mints the
// x-service-token the upstream requires on writes AND on the admin list read.
// ApiFetch attaches the Bearer token AND the x-tenant-id header from
// useAuthStore.selectedTenantId — carousel-service scopes WRITES (POST create,
// PATCH reorder) by that header, while the list read takes tenantId as a query
// param. The page therefore keeps BOTH merchant stores in sync when its selector
// changes, so the header always matches the merchant shown on screen.
import { ApiFetch } from '../../../shared/utils/fetch';

export type SlideType = 'ONE_ASSET' | 'COLLAGE';
export type SlidePage = 'HOME' | 'PLP' | 'PDP' | 'CUSTOM';
export type SlideAssetType = 'IMAGE' | 'VIDEO';

export const SLIDE_PAGES: readonly SlidePage[] = ['HOME', 'PLP', 'PDP', 'CUSTOM'];

/** Epic cap: a carousel — the (tenant, solution, page) group — holds at most 10 slides (an 11th create is rejected with 409). */
export const MAX_SLIDES_PER_CAROUSEL = 10;

/** Epic cap on slots per slide: 1 for ONE_ASSET, up to 4 for COLLAGE. */
export const MAX_COLLAGE_ASSETS = 4;

/**
 * PE-1177 enrichment carousel-service attaches to product-linked assets on reads
 * (best-effort — omitted when product-service is unreachable). Prices are in kobo.
 */
export interface SlideAssetProduct {
  id: string;
  name: string;
  imageUrl?: string | null;
  originalPrice: number;
  discountPercentage: number;
  discountedPrice: number;
}

/** Nested asset as returned by carousel-service (ordered by position). */
export interface CarouselSlideAsset {
  id: string;
  slideId: string;
  type: SlideAssetType;
  url: string;
  position: number;
  mediaId: string | null;
  productId: string | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
  createdAt: string;
  updatedAt: string;
  product?: SlideAssetProduct;
}

/**
 * Presentation values carried in the slide's `fields` Json. The ETL
 * (carousel-service prisma/etl) preserved every legacy slider field-value here,
 * so the known carrier keys are: headline, subheadline, bodyText, badgeText,
 * overlayOpacity, textPosition, assetPosition, plus the `_`-prefixed carriers
 * (_styleSlug = the retired slider styleSlug the storefront still maps layouts
 * from, _origPosition = pre-ETL sort position). Values migrated from the legacy
 * EAV rows are all strings on the wire (e.g. overlayOpacity "0.4"), so readers
 * must coerce — use fieldString/fieldNumber below.
 */
export type SlideFields = Record<string, unknown>;

/** Read a fields value as a display string ('' when absent). */
export const fieldString = (fields: SlideFields | null | undefined, key: string): string => {
  const value = fields?.[key];
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
};

/** Read a fields value as a finite number (null when absent/uncoercible — legacy values are strings). */
export const fieldNumber = (fields: SlideFields | null | undefined, key: string): number | null => {
  const value = fields?.[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

/** A slide as returned by carousel-service (nested assets ordered by position). */
export interface CarouselSlide {
  id: string;
  tenantId: string;
  solutionId: string;
  page: SlidePage;
  customPageSlug: string | null;
  position: number;
  type: SlideType;
  arrangement: string | null;
  ctaLink: string | null;
  ctaText: string | null;
  fields: SlideFields | null;
  isActive: boolean;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
  updatedAt: string;
  assets: CarouselSlideAsset[];
}

/** Asset input (SlideAssetInputDto). Omit empty optionals rather than sending nulls. */
export interface SlideAssetInput {
  type?: SlideAssetType;
  url: string;
  position?: number;
  mediaId?: string;
  productId?: string;
  ctaLabel?: string;
  ctaUrl?: string;
}

/**
 * CreateCarouselSlideDto. The owning tenant travels as the x-tenant-id header
 * (attached by ApiFetch), NOT in the body. `position` defaults upstream to the
 * current group size (appended to the end) and `page` defaults to HOME.
 */
export interface CreateCarouselSlidePayload {
  solutionId: string;
  page?: SlidePage;
  customPageSlug?: string;
  position?: number;
  type: SlideType;
  arrangement?: string;
  ctaLink?: string;
  ctaText?: string;
  fields?: SlideFields;
  isActive?: boolean;
  startsAt?: string;
  endsAt?: string;
  assets: SlideAssetInput[];
}

/**
 * UpdateCarouselSlideDto (PartialType). Explicit nulls CLEAR the nullable columns
 * (arrangement/ctaLink/ctaText/customPageSlug/startsAt/endsAt); omitted keys are
 * left untouched. Supplying `assets` replaces the slide's assets wholesale.
 */
export interface UpdateCarouselSlidePayload {
  solutionId?: string;
  page?: SlidePage;
  customPageSlug?: string | null;
  position?: number;
  type?: SlideType;
  arrangement?: string | null;
  ctaLink?: string | null;
  ctaText?: string | null;
  fields?: SlideFields;
  isActive?: boolean;
  startsAt?: string | null;
  endsAt?: string | null;
  assets?: SlideAssetInput[];
}

/** ReorderSlidesDto — slideIds must be the COMPLETE ordered list for the (tenant, solution, page) group. */
export interface ReorderSlidesPayload {
  solutionId: string;
  page?: SlidePage;
  slideIds: string[];
}

export const carouselSlidesApi = {
  /**
   * Admin list — carousel-service's service-token-guarded GET /carousel-slides/admin
   * (the osprey-api wildcard proxy forwards the sub-path and mints the token on every
   * verb). Returns the FULL (tenant, solution, page) group ordered by position —
   * inactive and out-of-schedule slides included — so the admin view is complete on
   * fresh load and reorder payloads can be built from the whole group (the upstream's
   * unique position index needs the COMPLETE id list).
   */
  list: (tenantId: string, solutionId: string, page: SlidePage): Promise<CarouselSlide[]> =>
    ApiFetch.get<CarouselSlide[]>(
      `/carousel-slides/admin?tenantId=${encodeURIComponent(tenantId)}&solutionId=${encodeURIComponent(solutionId)}&page=${page}`,
    ),
  create: (data: CreateCarouselSlidePayload): Promise<CarouselSlide> =>
    ApiFetch.post<CarouselSlide>('/carousel-slides', data),
  // ApiFetch.put issues a PATCH (see fetch.ts).
  update: (id: string, data: UpdateCarouselSlidePayload): Promise<CarouselSlide> =>
    ApiFetch.put<CarouselSlide>(`/carousel-slides/${id}`, data),
  remove: (id: string): Promise<{ message: string }> =>
    ApiFetch.del<{ message: string }>(`/carousel-slides/${id}`),
  /**
   * Upstream re-sequences positions in two phases inside a tenant transaction (so
   * the unique position index is never transiently violated) and returns the FULL
   * group — active or not — in the new order with nested assets.
   */
  reorder: (data: ReorderSlidesPayload): Promise<CarouselSlide[]> =>
    ApiFetch.put<CarouselSlide[]>('/carousel-slides/reorder', data),
};
